import { useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';

import { setUnauthorizedHandler } from '@/lib/api';
import { ToastProvider } from '@/components/ui/Toast';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { RedirectIfAuthenticated, RequireAuth } from '@/components/RequireAuth';
import AuthLayout from '@/components/layouts/AuthLayout';
import DashboardLayout from '@/components/layouts/DashboardLayout';

import LoginPage from '@/pages/Login';
import SignupPage from '@/pages/Signup';
import DashboardPage from '@/pages/Dashboard';
import TrafficPage from '@/pages/Traffic';
import CardsPage from '@/pages/Cards';
import FinancePage from '@/pages/Finance';
import VerificationPage from '@/pages/Verification';
import SettingsPage from '@/pages/Settings';
import NotFoundPage from '@/pages/NotFound';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // A 4xx will not fix itself; only retry transport and server faults.
        if (axios.isAxiosError(error)) {
          const status = error.response?.status;
          if (status !== undefined && status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/** Routes live in their own component so they can use router hooks. */
function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  // A 401 anywhere should land on /login without a full page reload, keeping
  // the intended destination so the trader is returned to it after signing in.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      queryClient.clear();
      navigate('/login', { replace: true, state: { from: location } });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate, location]);

  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      <Routes>
        <Route element={<RedirectIfAuthenticated />}>
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
          </Route>
        </Route>

        <Route element={<RequireAuth />}>
          <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/traffic" element={<TrafficPage />} />
            <Route path="/cards" element={<CardsPage />} />
            {/* Финансы deliberately has no tab: the owner wants three, and this
                is a screen a trader opens on purpose — to withdraw, or to work
                out where a figure came from — reached from the balances panel
                on Обзор. */}
            <Route path="/finance" element={<FinancePage />} />
            {/* Off the tab bar since the cards moved out of it, but still a
                real route: it is linked from the overview while verification is
                unfinished, and old bookmarks point at it. */}
            <Route path="/verification" element={<VerificationPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            {/* Legacy path kept so old bookmarks still resolve. */}
            <Route path="/kyc" element={<Navigate to="/verification" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </RouteErrorBoundary>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
